import React, { useState, useRef, useMemo } from 'react';
import {
  Box,
  Button,
  Paper,
  Chip,
  IconButton,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Tooltip,
  Divider,
  List,
  ListItem,
  ListItemText,
  Alert,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Add, Edit, Delete, Check, Close, Home, Warning, Cancel, Lock, Share, Upload, Download, Sell, People } from '@mui/icons-material';
import api from '../../utils/api';
import { Property, Role } from '../../types';
import { useAuth } from '../../contexts/AuthContext';
import { getPropertyPermissions } from '../../utils/permissionUtils';
import {
  useProperties,
  useDeleteProperty,
  useApproveProperty,
  useRejectProperty,
  useUpdateProperty,
} from '../../hooks/useApiCache';
import ConfirmDialog from '../../components/Common/ConfirmDialog';
import ToastNotification from '../../utils/toastNotification';
import AddPropertyDialog from '../../components/Properties/AddPropertyDialog';
import EditPropertyDialog from '../../components/Properties/EditPropertyDialog';
import PropertyDetailsDialog from '../../components/Properties/PropertyDetailsDialog';
import SharePropertyDialog from '../../components/Properties/SharePropertyDialog';
import GeneratePropertyLinkDialog from '../../components/Properties/GeneratePropertyLinkDialog';
import PropertyMatchingClientsDialog from '../../components/Properties/PropertyMatchingClientsDialog';
import DataTableFilter from '../../components/Common/DataTableFilter';
import PageHeader from '../../components/Common/PageHeader';
import { useDataFilter } from '../../hooks/useDataFilter';
import { PropertyType, PropertyStatus } from '../../types';

const PropertiesPage: React.FC = () => {
  const { user } = useAuth();
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [createLinkDialogOpen, setCreateLinkDialogOpen] = useState(false);
  const [matchingClientsDialogOpen, setMatchingClientsDialogOpen] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState<Property | null>(null);
  const [searchValue, setSearchValue] = useState('');
  const [filterValues, setFilterValues] = useState<Record<string, any>>({
    propertyTypes: [],
    status: '',
    minPrice: '',
    maxPrice: '',
  });
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; id: string | null }>({
    open: false,
    id: null,
  });
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [propertyToReject, setPropertyToReject] = useState<string | null>(null);
  const [sellDialogOpen, setSellDialogOpen] = useState(false);
  const [propertyToSell, setPropertyToSell] = useState<Property | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [importResultOpen, setImportResultOpen] = useState(false);
  const [importResult, setImportResult] = useState<{ successes: number; errors: { row: number; message: string }[] } | null>(null);
  const importFileInputRef = useRef<HTMLInputElement>(null);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [exportLoading, setExportLoading] = useState(false);

  // Get user permissions
  const permissions = user?.permissions ? getPropertyPermissions(user.permissions) : {
    canCreate: false,
    canRead: false,
    canUpdate: false,
    canDelete: false,
    canApprove: false,
    canReject: false,
  };

  // Use React Query for properties data
  const { data: propertiesData, isLoading: loading } = useProperties();

  // Mutation hooks
  const deletePropertyMutation = useDeleteProperty();
  const approvePropertyMutation = useApproveProperty();
  const rejectPropertyMutation = useRejectProperty();
  const updatePropertyMutation = useUpdateProperty();

  const properties = propertiesData || [];

  // Filter configuration
  const filterConfig = [
    {
      key: 'propertyTypes',
      label: 'Property Types',
      type: 'multiselect' as const,
      options: Object.values(PropertyType).map((type) => ({
        label: type,
        value: type,
      })),
    },
    {
      key: 'status',
      label: 'Status',
      type: 'select' as const,
      options: Object.values(PropertyStatus).map((status) => ({
        label: status,
        value: status,
      })),
    },
    {
      key: 'minPrice',
      label: 'Min Price',
      type: 'text' as const,
      placeholder: 'Minimum price',
    },
    {
      key: 'maxPrice',
      label: 'Max Price',
      type: 'text' as const,
      placeholder: 'Maximum price',
    },
  ];

  // Memoize filter options so useDataFilter's useMemo cache is not busted on every render
  const filterOptions = useMemo(() => ({
    searchFields: ['title', 'address', 'city', 'country'] as string[],
    filterFields: {
      propertyTypes: (item: any, value: any) => !value || (Array.isArray(value) && value.length === 0) || (Array.isArray(value) && item.propertyType && value.includes(item.propertyType)),
      status: (item: any, value: any) => !value || item.status === value,
      minPrice: (item: any, value: any) => {
        if (!value) return true;
        const v = parseFloat(value);
        if (!isFinite(v)) return true;
        return item.price >= v;
      },
      maxPrice: (item: any, value: any) => {
        if (!value) return true;
        const v = parseFloat(value);
        if (!isFinite(v)) return true;
        return item.price <= v;
      },
    },
  }), []);

  // Apply filters
  const filteredProperties = useDataFilter(properties, searchValue, filterValues, filterOptions);

  const handleApprove = async (id: string) => {
    try {
      await approvePropertyMutation.mutateAsync(id);
      console.log('[PropertiesPage] âœ… Successfully approved property:', id);
    } catch (error) {
      console.error('Error approving property:', error);
    }
  };

  const handleReject = async (id: string) => {
    setPropertyToReject(id);
    setRejectDialogOpen(true);
  };

  const handleConfirmReject = async () => {
    if (!propertyToReject) return;

    try {
      setActionLoading(true);
      await rejectPropertyMutation.mutateAsync({
        id: propertyToReject,
        rejectionReason,
      });
      console.log('[PropertiesPage] âœ… Successfully rejected property:', propertyToReject);
      setRejectDialogOpen(false);
      setRejectionReason('');
      setPropertyToReject(null);
    } catch (error) {
      console.error('Error rejecting property:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const closeRejectDialog = () => {
    setRejectDialogOpen(false);
    setRejectionReason('');
    setPropertyToReject(null);
  };

  const openSellDialog = (property: Property) => {
    setPropertyToSell(property);
    setSellDialogOpen(true);
  };

  const closeSellDialog = () => {
    setSellDialogOpen(false);
    setPropertyToSell(null);
  };

  const handleMarkSold = async (soldBy: 'COMPANY' | 'ANOTHER_COMPANY') => {
    if (!propertyToSell) return;
    try {
      await updatePropertyMutation.mutateAsync({ id: propertyToSell.id, data: { status: PropertyStatus.SOLD, soldBy } });
      ToastNotification.success('Property marked as sold!');
      closeSellDialog();
    } catch (error: any) {
      ToastNotification.error(error.response?.data?.message || 'Failed to mark property as sold');
    }
  };

  const formatSoldBy = (soldBy?: string) => {
    if (!soldBy) return '—';
    if (soldBy === 'COMPANY') return 'Company';
    if (soldBy === 'ANOTHER_COMPANY') return 'Another Company';
    return soldBy;
  };

  const handleImportExcel = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setImportLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post('/import/properties', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const data = response.data;
      const successCount = Array.isArray(data.successes)
        ? data.successes.length
        : Array.isArray(data.created)
        ? data.created.length
        : typeof data.successes === 'number'
        ? data.successes
        : typeof data.created === 'number'
        ? data.created
        : 0;
      const errorList: { row: number; message: string }[] = Array.isArray(data.errors)
        ? data.errors.map((e: any) =>
            typeof e === 'string'
              ? { row: 0, message: e }
              : { row: e.row ?? 0, message: typeof e.message === 'string' ? e.message : JSON.stringify(e.message ?? e) }
          )
        : [];
      setImportResult({ successes: successCount, errors: errorList });
      setImportResultOpen(true);
    } catch (error: any) {
      const msg = error.response?.data?.message || error.message || 'Import failed';
      setImportResult({ successes: 0, errors: [{ row: 0, message: msg }] });
      setImportResultOpen(true);
    } finally {
      setImportLoading(false);
    }
  };


  const handleExportCSV = async () => {
    setExportLoading(true);
    try {
      const response = await api.get('/properties/export/csv', {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'properties.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting properties:', error);
      // You might want to show a toast notification here
    } finally {
      setExportLoading(false);
    }
  };

  const handleEdit = (property: Property) => {
    setSelectedProperty(property);
    setEditDialogOpen(true);
  };

  const handleViewDetails = (property: Property) => {
    setSelectedProperty(property);
    setDetailsDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!deleteDialog.id) return;

    try {
      await deletePropertyMutation.mutateAsync(deleteDialog.id);
      setDeleteDialog({ open: false, id: null });
      ToastNotification.success('Property deleted successfully!');
    } catch (error: any) {
      console.error('Error deleting property:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to delete property';
      ToastNotification.error(errorMessage);
      setDeleteDialog({ open: false, id: null });
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'APPROVED':
        return 'success';
      case 'PENDING':
        return 'warning';
      case 'REJECTED':
        return 'error';
      case 'SOLD':
        return 'info';
      case 'RENTED':
        return 'secondary';
      default:
        return 'default';
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'title',
      headerName: 'Property',
      flex: 1,
      minWidth: 220,
      renderCell: (params) => (
        <Box 
          sx={{ 
            py: 1,
            cursor: 'pointer',
            '&:hover': {
              opacity: 0.8,
            },
            transition: 'opacity 0.2s ease',
          }}
          onClick={() => handleViewDetails(params.row)}
        >
          <Typography 
            variant="body2" 
            fontWeight={700} 
            sx={{ 
              color: '#1967d2', 
              mb: 0.3,
              textDecoration: 'underline',
              textDecorationColor: '#1967d2',
              textDecorationThickness: '2px',
              textUnderlineOffset: '3px',
            }}
          >
            {params.row.title}
          </Typography>
          <Typography variant="caption" sx={{ color: '#666', display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box component="span" sx={{ color: '#999' }}>ðŸ“</Box>
            {params.row.city}, {params.row.country}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'propertyType',
      headerName: 'Type',
      width: 130,
      renderCell: (params) => (
        <Chip
          label={params.row.propertyType}
          size="small"
          variant="outlined"
          sx={{
            fontWeight: 600,
            backgroundColor: 'rgba(25, 103, 210, 0.08)',
            borderColor: '#1967d2',
            color: '#1967d2',
          }}
        />
      ),
    },
    {
      field: 'price',
      headerName: 'Price',
      width: 140,
      renderCell: (params) => (
        <Typography sx={{ fontWeight: 700, color: '#1967d2', fontSize: '0.95rem' }}>
          ${params.row.price.toLocaleString()}
        </Typography>
      ),
    },
    {
      field: 'size',
      headerName: 'Size (mÂ²)',
      width: 110,
      renderCell: (params) => (
        <Typography sx={{ fontWeight: 600, color: '#333' }}>
          {params.row.size ? `${params.row.size}` : 'â€”'}
        </Typography>
      ),
    },
    {
      field: 'bedrooms',
      headerName: 'Beds',
      width: 90,
      renderCell: (params) => (
        <Chip
          label={params.row.bedrooms || 'â€”'}
          size="small"
          sx={{
            backgroundColor: params.row.bedrooms ? '#e3f2fd' : '#f5f5f5',
            color: params.row.bedrooms ? '#1967d2' : '#999',
            fontWeight: 600,
          }}
        />
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 280,
      renderCell: (params) => {
        const isPending = params.row.status === 'PENDING';

        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2, py: 0.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              {isPending && (
                <Tooltip title="Pending Approval" arrow>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 32,
                      height: 32,
                      backgroundColor: '#fff3e0',
                      borderRadius: '50%',
                      transition: 'all 0.3s ease',
                    }}
                  >
                    <Warning sx={{ fontSize: '1.4rem', color: '#e65100' }} />
                  </Box>
                </Tooltip>
              )}
            
              <Chip
                label={params.row.status}
                size="small"
                color={getStatusColor(params.row.status)}
                sx={{
                  fontWeight: 700,
                  fontSize: '0.8rem',
                  '& .MuiChip-label': {
                    px: 1.5,
                  },
                }}
              />
            </Box>
       
          </Box>
        );
      },
    },
    {
      field: 'soldBy',
      headerName: 'Sold By',
      width: 140,
      renderCell: (params) => (
        <Typography sx={{ fontWeight: 600, color: params.row.soldBy ? '#1967d2' : '#999', fontSize: '0.875rem' }}>
          {formatSoldBy(params.row.soldBy)}
        </Typography>
      ),
    },
    {
      field: 'agent',
      headerName: 'Agent',
      flex: 1,
      minWidth: 160,
      renderCell: (params) => {
        const agentName = params.row.agent?.user
          ? `${params.row.agent.user.firstName} ${params.row.agent.user.lastName}`
          : 'Unassigned';

        return (
          <Typography
            sx={{
              fontWeight: 600,
              color: params.row.agent ? '#1a1a1a' : '#999',
              fontSize: '0.9rem',
            }}
          >
            {agentName}
          </Typography>
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 270,
      sortable: false,
      renderCell: (params) => {
        const isPending = params.row.status === 'PENDING';
        const isAgent = user?.role === Role.ADMIN;
        const canEditThisProperty = permissions.canUpdate && !(isAgent && isPending);
        const canDeleteThisProperty = permissions.canDelete;
        const canApproveThisProperty = permissions.canApprove;
        const canRejectThisProperty = permissions.canReject;

        // If user has no permissions at all, show a locked state
        if (!canEditThisProperty && !canDeleteThisProperty && !canApproveThisProperty && !canRejectThisProperty) {
          return (
            <Tooltip title="You don't have permission to perform actions on this property" arrow>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#999' }}>
                <Lock sx={{ fontSize: '1.2rem' }} />
                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                  No Access
                </Typography>
              </Box>
            </Tooltip>
          );
        }

        return (
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {user?.role === Role.SUPER_ADMIN && isPending && (
              <>
                {canApproveThisProperty && (
                  <Tooltip title="Approve Property" arrow>
                    <IconButton
                      size="small"
                      sx={{
                        color: '#4caf50',
                        backgroundColor: 'rgba(76, 175, 80, 0.08)',
                        '&:hover': {
                          backgroundColor: 'rgba(76, 175, 80, 0.15)',
                        },
                        transition: 'all 0.3s ease',
                      }}
                      onClick={() => handleApprove(params.row.id)}
                    >
                      <Check sx={{ fontSize: '1.2rem' }} />
                    </IconButton>
                  </Tooltip>
                )}
                {canRejectThisProperty && (
                  <Tooltip title="Reject Property" arrow>
                    <IconButton
                      size="small"
                      sx={{
                        color: '#f44336',
                        backgroundColor: 'rgba(244, 67, 54, 0.08)',
                        '&:hover': {
                          backgroundColor: 'rgba(244, 67, 54, 0.15)',
                        },
                        transition: 'all 0.3s ease',
                      }}
                      onClick={() => handleReject(params.row.id)}
                    >
                      <Close sx={{ fontSize: '1.2rem' }} />
                    </IconButton>
                  </Tooltip>
                )}
              </>
            )}
            <Tooltip title="View Interested Clients" arrow>
              <IconButton
                size="small"
                sx={{
                  color: '#0097a7',
                  backgroundColor: 'rgba(0, 151, 167, 0.08)',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 151, 167, 0.15)',
                  },
                  transition: 'all 0.3s ease',
                }}
                onClick={() => {
                  setSelectedProperty(params.row);
                  setMatchingClientsDialogOpen(true);
                }}
              >
                <People sx={{ fontSize: '1.2rem' }} />
              </IconButton>
            </Tooltip>
            {canEditThisProperty && (
              <Tooltip title="Mark as Sold" arrow>
                <IconButton
                  size="small"
                  sx={{
                    color: '#9c27b0',
                    backgroundColor: 'rgba(156, 39, 176, 0.08)',
                    '&:hover': { backgroundColor: 'rgba(156, 39, 176, 0.15)' },
                    transition: 'all 0.3s ease',
                  }}
                  onClick={() => openSellDialog(params.row)}
                >
                  <Sell sx={{ fontSize: '1.2rem' }} />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Share Property" arrow>
              <IconButton
                size="small"
                sx={{
                  color: '#ff9800',
                  backgroundColor: 'rgba(255, 152, 0, 0.08)',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 152, 0, 0.15)',
                  },
                  transition: 'all 0.3s ease',
                }}
                onClick={() => {
                  setSelectedProperty(params.row);
                  setShareDialogOpen(true);
                }}
              >
                <Share sx={{ fontSize: '1.2rem' }} />
              </IconButton>
            </Tooltip>
            {canEditThisProperty ? (
              <Tooltip title={isAgent && isPending ? 'Cannot edit pending property' : 'Edit'} arrow>
                <span>
                  <IconButton
                    size="small"
                    sx={{
                      color: '#1967d2',
                      backgroundColor: 'rgba(25, 103, 210, 0.08)',
                      '&:hover': {
                        backgroundColor: 'rgba(25, 103, 210, 0.15)',
                      },
                      '&:disabled': {
                        color: '#ccc',
                        backgroundColor: '#f5f5f5',
                      },
                      transition: 'all 0.3s ease',
                    }}
                    onClick={() => handleEdit(params.row)}
                    disabled={isAgent && isPending}
                  >
                    <Edit sx={{ fontSize: '1.2rem' }} />
                  </IconButton>
                </span>
              </Tooltip>
            ) : (
              <Tooltip title="You don't have permission to edit properties" arrow>
                <span>
                  <IconButton
                    size="small"
                    disabled
                    sx={{
                      color: '#ccc',
                      backgroundColor: '#f5f5f5',
                    }}
                  >
                    <Edit sx={{ fontSize: '1.2rem' }} />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            {canDeleteThisProperty ? (
              <Tooltip title="Delete" arrow>
                <IconButton
                  size="small"
                  sx={{
                    color: '#f44336',
                    backgroundColor: 'rgba(244, 67, 54, 0.08)',
                    '&:hover': {
                      backgroundColor: 'rgba(244, 67, 54, 0.15)',
                    },
                    transition: 'all 0.3s ease',
                  }}
                  onClick={() => setDeleteDialog({ open: true, id: params.row.id })}
                >
                  <Delete sx={{ fontSize: '1.2rem' }} />
                </IconButton>
              </Tooltip>
            ) : (
              <Tooltip title="You don't have permission to delete properties" arrow>
                <span>
                  <IconButton
                    size="small"
                    disabled
                    sx={{
                      color: '#ccc',
                      backgroundColor: '#f5f5f5',
                    }}
                  >
                    <Delete sx={{ fontSize: '1.2rem' }} />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </Box>
        );
      },
    },
  ];

  return (
    <Box sx={{ width: '100%' }}>
      <PageHeader
        title="Properties Management"
        subtitle="Manage your property listings, track approvals, and view details"
        icon={<Home sx={{ fontSize: 'inherit', color: 'primary.main' }} />}
        breadcrumbs={[
          { label: 'Dashboard', path: '/' },
          { label: 'Properties' },
        ]}
        actions={
          permissions.canCreate ? (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => setAddDialogOpen(true)}
                sx={{
                  px: { xs: 1.5, sm: 3 },
                  fontWeight: 600,
                  textTransform: 'none',
                  fontSize: { xs: '0.8rem', sm: '1rem' },
                  boxShadow: '0 2px 8px rgba(25, 103, 210, 0.3)',
                  '& .MuiButton-startIcon': { mr: { xs: 0.5, sm: 1 } },
                  '&:hover': { boxShadow: '0 4px 16px rgba(25, 103, 210, 0.4)' },
                  transition: 'all 0.3s ease',
                }}
              >
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Add Property</Box>
                <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>Add</Box>
              </Button>
              <Button
                variant="outlined"
                startIcon={importLoading ? <CircularProgress size={16} /> : <Upload />}
                onClick={() => importFileInputRef.current?.click()}
                disabled={importLoading}
                sx={{
                  px: { xs: 1.5, sm: 3 },
                  fontWeight: 600,
                  textTransform: 'none',
                  fontSize: { xs: '0.8rem', sm: '1rem' },
                  borderColor: '#2e7d32',
                  color: '#2e7d32',
                  '& .MuiButton-startIcon': { mr: { xs: 0.5, sm: 1 } },
                  '&:hover': { backgroundColor: 'rgba(46, 125, 50, 0.08)' },
                }}
              >
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Import from Excel</Box>
                <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>Import</Box>
              </Button>
              <input
                ref={importFileInputRef}
                type="file"
                accept=".xlsx,.xls"
                style={{ display: 'none' }}
                onChange={handleImportExcel}
              />
              <Button
                variant="outlined"
                startIcon={exportLoading ? <CircularProgress size={16} /> : <Download />}
                onClick={handleExportCSV}
                disabled={exportLoading}
                sx={{
                  px: { xs: 1.5, sm: 3 },
                  fontWeight: 600,
                  textTransform: 'none',
                  fontSize: { xs: '0.8rem', sm: '1rem' },
                  borderColor: '#ff6f00',
                  color: '#ff6f00',
                  '& .MuiButton-startIcon': { mr: { xs: 0.5, sm: 1 } },
                  '&:hover': { backgroundColor: 'rgba(255, 111, 0, 0.08)' },
                }}
              >
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Export CSV</Box>
                <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>Export</Box>
              </Button>
              <Button
                variant="outlined"
                startIcon={<Share />}
                onClick={() => setCreateLinkDialogOpen(true)}
                sx={{
                  px: { xs: 1.5, sm: 3 },
                  fontWeight: 600,
                  textTransform: 'none',
                  fontSize: { xs: '0.8rem', sm: '1rem' },
                  borderColor: '#1967d2',
                  color: '#1967d2',
                  '& .MuiButton-startIcon': { mr: { xs: 0.5, sm: 1 } },
                  '&:hover': { backgroundColor: 'rgba(25, 103, 210, 0.08)' },
                }}
              >
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Creation Link</Box>
                <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>Link</Box>
              </Button>
            </Box>
          ) : (
            <Tooltip title="You don't have permission to create properties" arrow>
              <span>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  disabled
                  sx={{
                    px: { xs: 1.5, sm: 3 },
                    fontWeight: 600,
                    textTransform: 'none',
                    fontSize: { xs: '0.8rem', sm: '1rem' },
                  }}
                >
                  <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Add Property</Box>
                  <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>Add</Box>
                </Button>
              </span>
            </Tooltip>
          )
        }
      />

      {/* Filter Bar */}
      <Box sx={{ mb: 3 }}>
        <DataTableFilter
          filters={filterConfig}
          initialFilterValues={{ propertyTypes: [], status: '', minPrice: '', maxPrice: '' }}
          onFiltersChange={setFilterValues}
          onSearchChange={setSearchValue}
          searchPlaceholder="Search by property name, address, city..."
          showSearchBar={true}
          showAdvancedFilters={true}
          onRefresh={() => {
            setSearchValue('');
            setFilterValues({ propertyTypes: [], status: '', minPrice: '', maxPrice: '' });
          }}
          disabled={loading}
        />
      </Box>

      <Paper
        sx={{
          boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
          borderRadius: '12px',
          overflow: 'hidden',
          border: '1px solid rgba(0, 0, 0, 0.06)',
        }}
      >
        <DataGrid
          rows={filteredProperties}
          columns={columns}
          loading={loading}
          getRowId={(row) => row.id}
          pageSizeOptions={[10, 25, 50]}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          disableRowSelectionOnClick
          sx={{
            border: 'none',
            '& .MuiDataGrid-columnHeaderTitle': {
              fontWeight: 700,
              fontSize: '0.95rem',
              color: '#1a1a1a',
            },
            '& .MuiDataGrid-columnHeader': {
              backgroundColor: '#f8f9fa',
              borderBottom: '2px solid #e0e0e0',
              '&:hover': {
                backgroundColor: '#f0f2f5',
              },
            },
            '& .MuiDataGrid-cell': {
              borderBottom: '1px solid #e8e8e8',
              py: 1.5,
            },
            '& .MuiDataGrid-row': {
              '&:hover': {
                backgroundColor: '#f5f7fa',
                boxShadow: 'inset 0 0 0 1px rgba(0, 0, 0, 0.04)',
              },
              '&:last-child .MuiDataGrid-cell': {
                borderBottom: 'none',
              },
            },
            '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
              outline: 'none',
            },
            '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
              outline: 'none',
            },
            '& .MuiDataGrid-virtualScroller': {
              overflowX: 'auto',
            },
          }}
        />
      </Paper>

      <ConfirmDialog
        open={deleteDialog.open}
        title="Delete Property"
        message="Are you sure you want to delete this property? This action cannot be undone."
        onConfirm={handleDelete}
        onCancel={() => setDeleteDialog({ open: false, id: null })}
        confirmText="Delete"
        confirmColor="error"
      />

      <AddPropertyDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        onSuccess={(newProperty) => {
          if (newProperty) {
            console.log('[PropertiesPage] âœ… Successfully added property:', newProperty.title);
            // React Query handles the optimistic update and cache invalidation
          }
          setAddDialogOpen(false);
        }}
      />

      <EditPropertyDialog
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          setSelectedProperty(null);
        }}
        onSuccess={() => {
          console.log('[PropertiesPage] âœ… Successfully updated property');
          // React Query handles the optimistic update and cache invalidation
          setEditDialogOpen(false);
          setSelectedProperty(null);
        }}
        property={selectedProperty}
      />

      <PropertyDetailsDialog
        open={detailsDialogOpen}
        property={selectedProperty}
        onClose={() => {
          setDetailsDialogOpen(false);
          setSelectedProperty(null);
        }}
      />

      {/* Reject Dialog */}
      <Dialog
        open={rejectDialogOpen}
        onClose={closeRejectDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 700,
            fontSize: '1.25rem',
            color: '#1a1a1a',
            borderBottom: '1px solid #e0e0e0',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Cancel sx={{ color: '#f44336', fontSize: '1.5rem' }} />
          Reject Property
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography variant="body2" sx={{ color: '#666', mb: 2.5, lineHeight: 1.6 }}>
            Please provide a clear reason for rejecting this property. This information will be
            visible to the agent and help them understand what needs to be improved.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            multiline
            rows={4}
            label="Rejection Reason"
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            placeholder="e.g., Images are not clear, property details are incomplete, location data needs verification..."
            variant="outlined"
            sx={{
              '& .MuiOutlinedInput-root': {
                fontFamily: 'inherit',
                fontSize: '0.95rem',
                '& fieldset': {
                  borderColor: '#e0e0e0',
                },
                '&:hover fieldset': {
                  borderColor: '#1967d2',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#1967d2',
                  borderWidth: '2px',
                },
              },
              '& .MuiInputBase-input::placeholder': {
                color: '#999',
                opacity: 1,
              },
            }}
          />
          <Typography variant="caption" sx={{ color: '#999', mt: 1.5, display: 'block' }}>
            ðŸ’¡ Be specific to help the agent improve their listing
          </Typography>
        </DialogContent>
        <Divider sx={{ my: 0 }} />
        <DialogActions sx={{ p: 2.5, gap: 1 }}>
          <Button
            onClick={closeRejectDialog}
            disabled={actionLoading}
            sx={{
              color: '#666',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '1rem',
              '&:hover': {
                backgroundColor: '#f5f5f5',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmReject}
            variant="contained"
            sx={{
              backgroundColor: '#f44336',
              color: 'white',
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '1rem',
              px: 3,
              borderRadius: '6px',
              '&:hover': {
                backgroundColor: '#d32f2f',
                boxShadow: '0 4px 12px rgba(244, 67, 54, 0.3)',
              },
              '&:disabled': {
                backgroundColor: '#ccc',
                color: '#999',
              },
              transition: 'all 0.3s ease',
            }}
            disabled={!rejectionReason.trim() || actionLoading}
          >
            {actionLoading ? (
              <CircularProgress size={24} sx={{ color: 'white' }} />
            ) : (
              'Reject Property'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Share Property Dialog */}
      {selectedProperty && (
        <SharePropertyDialog
          open={shareDialogOpen}
          onClose={() => {
            setShareDialogOpen(false);
            setSelectedProperty(null);
          }}
          propertyId={selectedProperty.id}
          propertyTitle={selectedProperty.title}
        />
      )}

      <GeneratePropertyLinkDialog
        open={createLinkDialogOpen}
        onClose={() => setCreateLinkDialogOpen(false)}
        onLinkCreated={() => {
          // Optionally show a toast notification here
        }}
      />

      <PropertyMatchingClientsDialog
        open={matchingClientsDialogOpen}
        onClose={() => {
          setMatchingClientsDialogOpen(false);
          setSelectedProperty(null);
        }}
        property={selectedProperty}
      />

      {/* Mark Property as Sold Dialog */}
      <Dialog open={sellDialogOpen} onClose={closeSellDialog} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: '12px' } }}>
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Sell sx={{ color: '#9c27b0' }} />
          Mark Property as Sold
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Who sold <strong>{propertyToSell?.title}</strong>?
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button fullWidth variant="contained"
              sx={{ backgroundColor: '#1967d2', textTransform: 'none', fontWeight: 700, '&:hover': { backgroundColor: '#1557b0' } }}
              onClick={() => handleMarkSold('COMPANY')}>
              Company
            </Button>
            <Button fullWidth variant="outlined"
              sx={{ borderColor: '#9c27b0', color: '#9c27b0', textTransform: 'none', fontWeight: 700, '&:hover': { backgroundColor: 'rgba(156,39,176,0.08)' } }}
              onClick={() => handleMarkSold('ANOTHER_COMPANY')}>
              Another Company
            </Button>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeSellDialog} sx={{ textTransform: 'none', color: '#666' }}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Import Result Dialog */}
      <Dialog open={importResultOpen} onClose={() => setImportResultOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Import Result</DialogTitle>
        <DialogContent>
          {importResult && (
            <Box>
              <Alert severity={importResult.errors.length === 0 ? 'success' : 'warning'} sx={{ mb: 2 }}>
                {importResult.successes} propert{importResult.successes === 1 ? 'y' : 'ies'} imported successfully.
                {importResult.errors.length > 0 && ` ${importResult.errors.length} row(s) had errors.`}
              </Alert>
              {importResult.errors.length > 0 && (
                <>
                  <Typography variant="subtitle2" gutterBottom>Errors:</Typography>
                  <List dense sx={{ maxHeight: 300, overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: 1 }}>
                    {importResult.errors.map((err, idx) => (
                      <ListItem key={idx} divider>
                        <ListItemText
                          primary={err.row ? `Row ${err.row}` : 'Error'}
                          secondary={err.message}
                          primaryTypographyProps={{ variant: 'caption', fontWeight: 600 }}
                          secondaryTypographyProps={{ variant: 'caption', color: 'error' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportResultOpen(false)} variant="contained">Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PropertiesPage;

